import { z } from 'zod';
import { marketPresentationSchema } from './market-presentation.js';
import { historicalMatchSummarySchema } from './football-history.js';
import { matchCardSchema, opaquePublicIdSchema, playerRefSchema, publicEnvelopeSchema, publicSectionSchema, teamRefSchema, } from './football-public.js';
const timelineEventSchema = z.object({
    eventId: opaquePublicIdSchema,
    minute: z.number().int().min(0).max(130).nullable(),
    stoppageMinute: z.number().int().min(0).max(30).nullable(),
    period: z.enum(['first_half', 'half_time', 'second_half', 'extra_time', 'penalties', 'unknown']),
    kind: z.enum(['goal', 'own_goal', 'penalty_goal', 'missed_penalty', 'yellow_card', 'red_card', 'substitution', 'var', 'other']),
    teamId: opaquePublicIdSchema.nullable(),
    primaryPlayer: playerRefSchema.nullable(),
    secondaryPlayer: playerRefSchema.nullable(),
    scoreAfter: z.object({ home: z.number().int().min(0), away: z.number().int().min(0) }).strip().nullable(),
}).strip();
const lineupSideSchema = z.object({
    team: teamRefSchema,
    announcement: z.enum(['announced', 'pending', 'not_covered']),
    formation: z.string().max(50).nullable(),
    players: z.array(z.object({
        player: playerRefSchema,
        role: z.enum(['starter', 'substitute']),
        shirtNumber: z.number().int().min(0).max(99).nullable(),
        captain: z.boolean(),
    }).strip()),
    totalPlayers: z.number().int().min(0),
    truncated: z.boolean(),
}).strip();
const formSummarySchema = z.object({
    team: teamRefSchema,
    sampleSize: z.number().int().min(0),
    wins: z.number().int().min(0),
    draws: z.number().int().min(0),
    losses: z.number().int().min(0),
    goalsFor: z.number().int().min(0),
    goalsAgainst: z.number().int().min(0),
}).strip();
export const footballMatchDetailDataSchema = z.object({
    match: matchCardSchema.extend({
        roundName: z.string().max(160).nullable(),
        venueName: z.string().max(200).nullable(),
        venueCity: z.string().max(200).nullable(),
    }),
    timeline: publicSectionSchema(z.object({
        items: z.array(timelineEventSchema),
        totalItems: z.number().int().min(0),
        truncated: z.boolean(),
    }).strip()),
    lineups: publicSectionSchema(z.object({ home: lineupSideSchema, away: lineupSideSchema }).strip()),
    form: publicSectionSchema(z.object({ home: formSummarySchema, away: formSummarySchema }).strip()),
    headToHead: publicSectionSchema(z.object({
        sampleSize: z.number().int().min(0),
        matches: z.array(historicalMatchSummarySchema),
        truncated: z.boolean(),
    }).strip()),
    marketPresentation: marketPresentationSchema,
}).strip();
export const footballMatchDetailResponseSchema = publicEnvelopeSchema(footballMatchDetailDataSchema);
export const footballMatchDetailParamsSchema = z.object({
    matchId: opaquePublicIdSchema,
}).strict();
export const footballMatchDetailQuerySchema = z.object({
    timelineLimit: z.coerce.number().int().min(1).max(100).optional().default(20),
    lineupLimit: z.coerce.number().int().min(1).max(50).optional().default(18),
    recentFormLimit: z.coerce.number().int().min(1).max(10).optional().default(5),
    headToHeadLimit: z.coerce.number().int().min(1).max(10).optional().default(5),
}).strict();
export const footballLegacyAliasParamsSchema = z.object({
    sourceKind: z.enum([
        'football_fixture',
        'api_football_fixture',
        'polymarket_event',
        'polymarket_game',
        'opportunity',
        'research_match',
    ]),
    sourceId: z.string().trim().min(1).max(2048),
}).strict();
export const footballLegacyAliasResolutionSchema = z.discriminatedUnion('disposition', [
    z.object({
        disposition: z.literal('canonical'),
        matchId: opaquePublicIdSchema,
        stability: z.enum(['promotable', 'terminal']),
    }).strip(),
    z.object({
        disposition: z.literal('historical'),
        researchMatchId: opaquePublicIdSchema,
        stability: z.literal('promotable'),
    }).strip(),
]);
export const footballLegacyAliasResponseSchema = publicEnvelopeSchema(footballLegacyAliasResolutionSchema);
